import type { BusinessCalendar } from '../../business-calendars/platform-business-calendars.models';

export interface ScheduleParameterField {
  name: string;
  type: string;
  required: boolean;
  nullable: boolean;
  maxLength?: number | null;
  values?: string[];
  items?: ScheduleParameterField[];
  maxItems?: number | null;
  recordTypeCode?: string | null;
}
export interface ScheduleParameterSchema {
  code: string;
  version: number;
  hash: string;
  fields: ScheduleParameterField[];
}
export interface RecurringScheduleDefinition {
  code: string;
  version: number;
  label: string;
  triggerCode: string;
  triggerVersion: number;
  triggerHash: string;
  parameters: Record<string, unknown>;
  calendarCode: string;
  calendarVersionCode: string;
  calendarContentHash: string;
  timeZoneId: string;
  startDate: string;
  endDate: string | null;
  localTime: string;
  intervalDays: number;
  workingDaysOnly: boolean;
  gapPolicy: string;
  ambiguousTimePolicy: string;
  misfirePolicy: string;
  catchUpLimit: number;
  overlapPolicy: string;
  workflowSelectionPolicy: string;
  configurationSelectionPolicy: string;
}
export interface RecurringScheduleVersion {
  id: number;
  revision: number;
  eTag: string;
  statusCode: 'draft' | 'published';
  definition: RecurringScheduleDefinition;
  contentHash: string;
  publishedUtc: string | null;
}
export interface RecurringScheduleControl {
  code: string;
  revision: number;
  eTag: string;
  publishedVersionId: number | null;
  enabled: boolean;
  nextLocalDate: string | null;
  existingWorkPolicy: string;
  lastReason: string | null;
  updatedUtc?: string | null;
  issueCode?: string | null;
}
export interface RecurringScheduleEntryPoint {
  triggerCode: string;
  workflowCode: string;
  candidateSelector: string;
  enrollmentBehavior: string;
  preparationStepCode: string;
  workerEnabled: boolean;
}
export interface RecurringScheduleTarget {
  entryPoint?: RecurringScheduleEntryPoint | null;
  code: string;
  version: number;
  hash: string;
  label: string;
  registration: {
    payloadSchema: ScheduleParameterSchema;
    sourceSystem: string;
    sourceKind: string;
    eventType: string;
    serviceExternalIdentityId: number;
    serviceTenantId: string;
    dataScope: { scopeTypeCode: string; scopeKey: string | null };
    ownerScope: { scopeTypeCode: string; scopeKey: string | null };
    moduleCode: string;
    moduleVersion: number;
    moduleHash: string;
    command: {
      key: { provider: string; kind: string; code: string; version: number };
      inputSchemaHash: string;
      outputSchemaHash: string;
      contractHash: string;
    };
  };
}
export interface RecurringScheduleCatalog {
  contractVersion: 1;
  producerEnabled: boolean;
  targets: RecurringScheduleTarget[];
  calendars: BusinessCalendar[];
  versions: RecurringScheduleVersion[];
  controls: RecurringScheduleControl[];
  historyTruncated?: boolean;
}
export type ScheduleRecoveryAction = 'retry' | 'rerun';
export interface RecurringScheduleRun {
  runKind?: string;
  id: string;
  code: string;
  definitionVersion: number;
  localDate: string;
  nominalUtc: string | null;
  dueUtc: string;
  status: string;
  eTag: string;
  attempts: number;
  leaseUntilUtc: string | null;
  issueCode: string | null;
  invocationId: string | null;
  deliveryStatus: string | null;
  rerunOf: string | null;
  reason: string | null;
  recoveryActions: ScheduleRecoveryAction[];
  deliveryConsumerId: string | null;
  updatedUtc?: string | null;
  lastRecoveryReason?: string | null;
}
export interface ScheduleControlCommand {
  expectedRevision: number;
  publishedVersionId: number;
  enabled: boolean;
  existingWorkPolicy: string;
  reason: string;
}
export const scheduleTargetIdentity = (target: {
  code: string;
  version: number;
  hash: string;
}) => JSON.stringify([target.code, target.version, target.hash]);
export const scheduleCalendarIdentity = (
  calendar: Pick<BusinessCalendar, 'code' | 'versionCode' | 'contentHash'>,
) =>
  JSON.stringify([calendar.code, calendar.versionCode, calendar.contentHash]);

export function scheduleParameterErrors(
  fields: readonly ScheduleParameterField[],
  values: Record<string, unknown>,
  prefix = '',
): string[] {
  const errors: string[] = [];
  for (const name of Object.keys(values))
    if (!fields.some((field) => field.name === name))
      errors.push(`${prefix}${name}: not declared by the selected operation.`);
  for (const field of fields) {
    const value = values[field.name],
      label = `${prefix}${field.name}`;
    if (value === undefined) {
      if (field.required) errors.push(`${label}: supply a value.`);
      continue;
    }
    if (value === null) {
      if (!field.nullable) errors.push(`${label}: null is not permitted.`);
      continue;
    }
    if (field.type === 'array') {
      if (!Array.isArray(value) || value.length > (field.maxItems ?? 0))
        errors.push(
          `${label}: supply at most ${field.maxItems ?? 0} typed rows.`,
        );
      else
        value.forEach((row, index) => {
          if (!isParameterObject(row))
            errors.push(`${label}[${index + 1}]: a typed row is required.`);
          else
            errors.push(
              ...scheduleParameterErrors(
                field.items ?? [],
                row,
                `${label}[${index + 1}].`,
              ),
            );
        });
    } else if (field.type === 'string') {
      if (
        typeof value !== 'string' ||
        (field.maxLength != null && value.length > field.maxLength) ||
        (field.values?.length && !field.values.includes(value))
      )
        errors.push(
          `${label}: choose compatible text${field.maxLength ? ` (up to ${field.maxLength} characters)` : ''}.`,
        );
    } else if (field.type === 'integer') {
      if (
        typeof value !== 'number' ||
        !Number.isInteger(value) ||
        value < -2147483648 ||
        value > 2147483647
      )
        errors.push(`${label}: supply a whole number in the supported range.`);
    } else if (field.type === 'number') {
      if (typeof value !== 'number' || !Number.isFinite(value))
        errors.push(`${label}: supply a finite number.`);
    } else if (field.type === 'boolean') {
      if (typeof value !== 'boolean')
        errors.push(`${label}: choose Yes or No.`);
    } else errors.push(`${label}: this type is unavailable.`);
  }
  return errors;
}
export function isParameterObject(
  value: unknown,
): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
